import React, { useCallback, useEffect, useRef } from "react";
import AppLayout from "@/Layouts/AppLayout";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import { Teams as TeamsIcon } from "@/Icons";
import LayoutIcon from "@/Components/LayoutIcon";
import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import { usePayload } from "@/Contexts/PayloadContext";
import { toast } from "react-toastify";
import CalculatorFormPart from "@/Pages/Organization/Calculators/Partials/CalculatorFormPart";
import { isEmail } from "@/Utilities/Utilities";
import CalculatorAddActivitySidebar from "@/Pages/Organization/Calculators/Partials/CalculatorAddActivitySidebar";
import { LoadingProvider } from "@/Contexts/LoadingContext";

export default function Create() {
  const ref = useRef(null);

  return (
    <>
      <form ref={ref} className="mt-8 flex flex-col gap-4">
        <CalculatorFormPart />
        <Action formRef={ref} />
      </form>

      <LoadingProvider>
        <CalculatorAddActivitySidebar />
      </LoadingProvider>
    </>
  );
}

Create.layout = (page) => {
  const team = page.props?.team?.data;

  return (
    <AppLayout title="Create new calculator">
      <OrganizationLayout
        preamble={team.name}
        header="Create new calculator"
        backAction={{
          label: "Back",
          callback: () => Inertia.visit(route("teams")),
        }}
        icon={
          <LayoutIcon>
            <TeamsIcon size={40} className="text-black" />
          </LayoutIcon>
        }
      >
        {page}
      </OrganizationLayout>
    </AppLayout>
  );
};

const Action = ({ formRef }) => {
  const { payload, reset } = usePayload();
  const team = usePage().props?.team?.data;

  useEffect(() => {
    if (payload?.id) {
      console.log("resetting payload");
      reset();
    }
  }, []);

  const submit = useCallback(
    (e) => {
      e.preventDefault();

      if (isDisabled()) {
        return;
      }

      const id = toast.loading("Please wait...");

      let recipients = payload?.recipients || [];

      if (payload.recipients_input) {
        const emails = payload.recipients_input
          .split(",")
          .map((email) => email.trim().toLowerCase());
        const validEmails = emails.filter((email) => isEmail(email));

        recipients = [...recipients, ...validEmails];
      }

      let body = {
        name: payload?.name,
        start_date: payload?.start_date,
        end_date: payload?.end_date,
        expiry_date: payload?.expiry_date,
        description: payload?.description,
        questions: payload?.questions?.map((a, index) => ({
          id: a.id,
          order: index,
        })),
      };

      if (recipients.length > 0) {
        body.recipients = recipients;
      }

      const options = {};

      if (payload.logo_file !== false) {
        body.logo_file = payload.logo_file;
        if (payload.logo_file) {
          // An image has been uploaded, so we need to send the file as a FormData object
          options.forceFormData = true;
        }
      }

      Inertia.post(route("calculators.store", team), body, {
        onSuccess: () => {
          reset();
          toast.update(id, {
            render: "The calculator created successfully",
            type: "success",
            isLoading: false,
            autoClose: 5000,
          });
        },
        onError: () => {
          toast.update(id, {
            render:
              "An error occurred. Please check your fields and try again.",
            type: "error",
            isLoading: false,
            autoClose: 5000,
          });
        },
      });
    },
    [payload],
  );

  useEffect(() => {
    formRef.current?.addEventListener("submit", submit);
    return () => {
      formRef.current?.removeEventListener("submit", submit);
    };
  }, [formRef, submit]);

  const isDisabled = useCallback(
    () => formRef.current?.checkValidity() !== true,
    [formRef],
  );

  return (
    <div className="mt-4 flex w-full items-center justify-end gap-4">
      <InertiaLink
        className="cursor-pointer text-blue underline hover:no-underline"
        href={route("team.overview", {
          team: team,
          tab: 2,
        })}
      >
        Cancel
      </InertiaLink>
      <button className="btn-blue grow-0" disabled={isDisabled()}>
        Create calculator
      </button>
    </div>
  );
};
